import { ChangeDetectionStrategy, Component, HostListener, OnDestroy, OnInit, computed, effect, forwardRef, inject, signal, untracked } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { JsonPipe, KeyValuePipe } from '@angular/common';
import { TranslocoDirective } from '@jsverse/transloco';
import { catchError, interval, of, Subscription } from 'rxjs';

import { FeedPage } from '../shared/feed-page';
import { FeedPageHeaderComponent } from '../shared/feed-page-header.component';
import { FeedBasicsCardComponent } from '../shared/feed-basics-card.component';
import { FeedIdentityCardComponent } from '../shared/feed-identity-card.component';
import { FeedFilesComponent } from '../shared/feed-files.component';
import { FeedTab, FeedTabStripComponent } from '../shared/feed-tab-strip.component';
import { StackedBar, StackedBarSegment } from '../../../shared/components/stacked-bar/stacked-bar';
import { SIRI_PROVIDER_TYPE } from '../shared/provider-routing';
import { FormField } from '../../../shared/components/forms/form-field';
import { TextInput } from '../../../shared/components/forms/text-input';
import { Select, SelectOption } from '../../../shared/components/forms/select';
import { LabelBadge, LabelBadgeDot } from '../../../shared/components/label-badge/label-badge';
import { Icon } from '../../../shared/components/icon/icon';
import { FeedService } from '../../../shared/services/feed.service';
import { FeedDiagnostics, SiriDiagnosticsPayload } from '../../../shared/models/feed-diagnostics.model';
import { deriveFeedHealth, DIAGNOSTICS_POLL_CYCLE_MINUTES, FeedHealth, isSnapshotStale } from '../util/feed-diagnostics.util';
import { relativeTime } from '../../../shared/utils/relative-time';
import { ContentDeliveryService } from '../services/content-delivery.service';
import { StopContent } from '../../../shared/models/stop-content.model';
import {
  buildDepartureBoard,
  mergeLiveMessages,
  pickLocalizedText,
  sortMessagesByPriority,
  DepartureRow,
} from '../util/stop-content.util';
import { InformationMessage, JourneyMessage } from '../../../shared/models/stop-content.model';
import { LanguageService } from '../../../core/i18n/language.service';

/** Drawer auto-refreshes the fetched stop content at this cadence, counting
 *  from the last completed fetch (manual Refresh resets the countdown). */
const REFRESH_SECONDS = 30;

export type DrawerTab = 'departures' | 'messages' | 'raw';

export interface StopDrawerState {
  stopId: string;
  diagnosticsStatus: string;
}

const HEALTH_DOT: Record<FeedHealth['status'], LabelBadgeDot | null> = {
  'no-snapshot': null,
  failing: 'red',
  'not-reporting': 'orange',
  ok: 'green',
};

const FLAVOR_DEFAULT = 'SIRI_2_0_XML';
const REQUEST_METHOD_DEFAULT = 'POST';

const FLAVOR_OPTIONS: SelectOption[] = [
  { value: 'SIRI_2_0_XML', label: 'SIRI_2_0_XML' },
  { value: 'SIRI_LITE', label: 'SIRI_LITE' },
];
const REQUEST_METHOD_OPTIONS: SelectOption[] = [
  { value: 'POST', label: 'POST' },
  { value: 'GET', label: 'GET' },
];

export type SiriFeedTab = 'general' | 'connection' | 'stops' | 'files';

const SIRI_TABS: readonly SiriFeedTab[] = ['general', 'connection', 'stops', 'files'];

/**
 * SIRI feed page (/admin/providers/siri/:providerId/:feedId, 'new' = create),
 * per design_handoff_gtfs_provider v3 (3c): the shared feed header, then a
 * centred [main card | 380px column] grid. The main card holds the tabs —
 * General (name, location) · Connection (the fixed SIRI keys: endpoint,
 * requestorRef, auth, flavor, requestMethod) · Stops (the diagnostics stop
 * table; badge = errored stops) · Files (only when the provider accepts
 * files). The sticky right column holds a Feed health card (stop and arrival
 * totals + an OK/Error/Unknown stacked bar) and the shared Identity card.
 * Clicking a stop opens the stop detail drawer (live content delivery for one
 * stop). Shared feed state/save/delete come from `FeedPage`; the active tab is
 * mirrored in `?tab=`.
 */
@Component({
  selector: 'app-siri-feed-page',
  standalone: true,
  imports: [
    FormsModule,
    TranslocoDirective,
    JsonPipe,
    KeyValuePipe,
    FormField,
    TextInput,
    Select,
    LabelBadge,
    Icon,
    FeedPageHeaderComponent,
    FeedBasicsCardComponent,
    FeedIdentityCardComponent,
    FeedFilesComponent,
    FeedTabStripComponent,
    StackedBar,
  ],
  templateUrl: './siri-feed-page.component.html',
  styleUrl: './siri-feed-page.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [{ provide: FeedPage, useExisting: forwardRef(() => SiriFeedPageComponent) }],
  host: { class: 'block h-full min-h-0' },
})
export class SiriFeedPageComponent extends FeedPage implements OnInit, OnDestroy {
  private readonly feedService = inject(FeedService);
  private readonly contentDeliveryService = inject(ContentDeliveryService);
  private readonly languageService = inject(LanguageService);

  readonly routeType = SIRI_PROVIDER_TYPE;

  readonly activeTab = signal<SiriFeedTab>(this.tabFromUrl());

  /** Mirror the tab in ?tab= (general = no param) without adding history entries. */
  private readonly syncTabToUrl = effect(() => {
    const tab = this.activeTab();
    untracked(() => {
      const current = this.router.parseUrl(this.router.url).queryParamMap.get('tab') ?? 'general';
      if (current === tab) return; // already in sync (e.g. on load) — no extra navigation
      void this.router.navigate([], {
        relativeTo: this.route,
        queryParams: { tab: tab === 'general' ? null : tab },
        queryParamsHandling: 'merge',
        replaceUrl: true,
      });
    });
  });

  readonly tabs = computed<FeedTab<SiriFeedTab>[]>(() => {
    this.languageService.current(); // re-evaluate on language switch
    const t = (k: string) => this.transloco.translate(`providers.feedPage.tabs.${k}`);
    const tabs: FeedTab<SiriFeedTab>[] = [
      { id: 'general', label: t('general'), badge: this.nameTaken() ? 1 : 0 },
      { id: 'connection', label: t('connection'), badge: 0 },
      { id: 'stops', label: t('stops'), badge: this.health().error },
    ];
    // Files only for providers that accept them (core 400s otherwise), and only once the feed exists.
    if (!this.isCreate && this.provider()?.acceptsFiles) tabs.push({ id: 'files', label: t('files'), badge: this.feed()?.files?.failing ?? 0 });
    return tabs;
  });

  /** OK / Error / Unknown stop counts for the Feed health card's bar. */
  readonly stopSegments = computed<StackedBarSegment[]>(() => {
    this.languageService.current();
    const h = this.health();
    const t = (k: string) => this.transloco.translate(`providers.diagnostics.counts.${k}`);
    return [
      { label: t('ok'), count: h.ok, tone: 'ok' },
      { label: t('error'), count: h.error, tone: 'error' },
      { label: t('unknown'), count: h.unknown, tone: 'warn' },
    ];
  });

  /** Whole minutes since the snapshot was generated (for the stale box). */
  readonly staleMinutes = computed(() => {
    const d = this.diagnostics();
    return d ? Math.max(0, Math.floor((Date.now() - new Date(d.generatedAt).getTime()) / 60_000)) : 0;
  });

  selectTab(tab: string): void {
    if ((SIRI_TABS as readonly string[]).includes(tab)) this.activeTab.set(tab as SiriFeedTab);
  }

  private tabFromUrl(): SiriFeedTab {
    const tab = this.route.snapshot.queryParamMap.get('tab') ?? '';
    return (SIRI_TABS as readonly string[]).includes(tab) ? (tab as SiriFeedTab) : 'general';
  }

  /** null = not loaded yet / no snapshot (both indistinguishable here — a
   *  404 means "no snapshot", never an error, per FeedService). undefined
   *  briefly while the fetch is in flight, distinguished via isDiagnosticsLoading. */
  readonly diagnostics = signal<FeedDiagnostics<SiriDiagnosticsPayload> | null>(null);
  readonly isDiagnosticsLoading = signal(false);
  readonly showIssuesOnly = signal(false);

  readonly health = computed(() => deriveFeedHealth(this.diagnostics()));
  readonly healthDot = computed(() => HEALTH_DOT[this.health().status]);
  readonly isStale = computed(() => isSnapshotStale(this.diagnostics()));
  readonly visibleStops = computed(() => {
    const stops = this.diagnostics()?.payload?.stops ?? [];
    return this.showIssuesOnly() ? stops.filter((s) => s.status !== 'OK') : stops;
  });

  // --- Stop detail drawer ---------------------------------------------
  // UI state only (a signal, not a route) — see the design doc on
  // feed-detail.component.html's stops table row click handler.
  readonly stopDrawer = signal<StopDrawerState | null>(null);
  readonly content = signal<StopContent | null>(null);
  readonly drawerTab = signal<DrawerTab>('departures');
  readonly autoRefresh = signal(true);
  readonly fetchedAt = signal<number>(0);
  readonly isFetching = signal(false);
  readonly lastError = signal<unknown>(null);
  /** Seconds remaining until the next auto-refresh tick; ticks every second
   *  while the drawer is open, independent of autoRefresh (used to show
   *  "fetched Ns ago" while paused too). */
  readonly nowTick = signal(Date.now());

  private refreshTimerSub: Subscription | null = null;

  readonly isDrawerOpen = computed(() => this.stopDrawer() !== null);

  readonly departureRows = computed<DepartureRow[]>(() => {
    const c = this.content()?.content;
    if (!c) return [];
    return buildDepartureBoard(c, this.languageService.current());
  });

  readonly liveInformationMessages = computed<InformationMessage[]>(() => {
    const c = this.content()?.content;
    return sortMessagesByPriority(mergeLiveMessages<InformationMessage>(c?.informationMessages));
  });

  readonly liveJourneyMessages = computed<JourneyMessage[]>(() => {
    const c = this.content()?.content;
    return sortMessagesByPriority(mergeLiveMessages<JourneyMessage>(c?.journeyMessages));
  });

  readonly messagesCount = computed(() => this.liveInformationMessages().length + this.liveJourneyMessages().length);

  /** The only freshness timestamp confirmed on a live payload — nested under
   *  `content`, not the envelope. */
  readonly drawerPublishedAt = computed(() => {
    return this.content()?.content?.receivedAt ?? null;
  });

  /** Seconds remaining until the next auto-refresh tick, counted from the
   *  last completed fetch. Derived from nowTick/fetchedAt rather than its own
   *  interval so it stays in lockstep with the fetch that resets it. */
  readonly refreshCountdown = computed(() => {
    const elapsedSec = Math.floor((this.nowTick() - this.fetchedAt()) / 1000);
    return Math.max(0, REFRESH_SECONDS - elapsedSec);
  });

  readonly secondsSinceFetch = computed(() => Math.max(0, Math.floor((this.nowTick() - this.fetchedAt()) / 1000)));

  readonly sourceUrl = computed(() => {
    const drawer = this.stopDrawer();
    const f = this.feed();
    if (!drawer || !f) return '';
    return this.contentDeliveryService.stopContentUrl(f.tenantId, this.routeFeedId, drawer.stopId);
  });

  readonly flavorOptions = FLAVOR_OPTIONS;
  readonly requestMethodOptions = REQUEST_METHOD_OPTIONS;

  // SIRI connection fields (plain ngModel fields — see FeedPage.isValid)
  endpoint = '';
  requestorRef = '';
  auth = '';
  flavor: string = FLAVOR_DEFAULT;
  requestMethod: string = REQUEST_METHOD_DEFAULT;

  override ngOnInit(): void {
    super.ngOnInit();
    if (!this.isCreate) this.fetchDiagnostics();
  }

  protected configurationValid(): boolean {
    return !!this.endpoint.trim() && !!this.requestorRef.trim();
  }

  protected buildConfiguration(): Record<string, string> {
    const configuration: Record<string, string> = {
      endpoint: this.endpoint.trim(),
      flavor: this.flavor,
      requestorRef: this.requestorRef.trim(),
      requestMethod: this.requestMethod,
    };
    const auth = this.auth.trim();
    if (auth) configuration['auth'] = auth;
    return configuration;
  }

  protected applyConfiguration(configuration: Record<string, string> | null): void {
    const config = configuration ?? {};
    this.endpoint = config['endpoint'] ?? '';
    this.flavor = config['flavor'] || FLAVOR_DEFAULT;
    this.requestorRef = config['requestorRef'] ?? '';
    this.auth = config['auth'] ?? '';
    this.requestMethod = config['requestMethod'] || REQUEST_METHOD_DEFAULT;
  }

  readonly relativeTime = relativeTime;
  readonly pollCycleMinutes = DIAGNOSTICS_POLL_CYCLE_MINUTES;

  hasArrivals(arrivalsByRoute: Record<string, number>): boolean {
    return Object.keys(arrivalsByRoute).length > 0;
  }

  /** KeyValuePipe sorts by key by default; arrivalsByRoute should render "as
   *  delivered" (object insertion order), so this no-op comparator keeps
   *  Array.sort's stable pass-through instead. */
  unsortedKeyValue(): number {
    return 0;
  }

  toggleIssuesOnly(issuesOnly: boolean): void {
    this.showIssuesOnly.set(issuesOnly);
  }

  ngOnDestroy(): void {
    this.stopRefreshTimer();
  }

  /** Opens the drawer for a stop, resetting its tab and refresh countdown —
   *  called from the stops table row click. */
  openStopDrawer(stopId: string, diagnosticsStatus: string): void {
    this.stopDrawer.set({ stopId, diagnosticsStatus });
    this.drawerTab.set('departures');
    this.content.set(null);
    this.lastError.set(null);
    this.fetchDrawerContent();
    this.startRefreshTimer();
  }

  closeStopDrawer(): void {
    this.stopDrawer.set(null);
    this.stopRefreshTimer();
  }

  onDrawerScrimClick(): void {
    this.closeStopDrawer();
  }

  // Scoped to the drawer's own open state so Escape never bubbles into
  // closing anything else on the page (e.g. a modal) while the drawer is shut.
  @HostListener('document:keydown.escape')
  onEscapeKey(): void {
    if (this.isDrawerOpen()) this.closeStopDrawer();
  }

  setDrawerTab(tab: DrawerTab): void {
    this.drawerTab.set(tab);
  }

  toggleDrawerAutoRefresh(): void {
    this.autoRefresh.update((v) => !v);
  }

  refreshDrawerContent(): void {
    this.fetchDrawerContent();
  }

  async copyRawJson(): Promise<void> {
    const c = this.content();
    if (!c) return;
    try {
      await navigator.clipboard.writeText(JSON.stringify(c, null, 2));
      this.toastService.show(this.transloco.translate('providers.stopDrawer.raw.copied'), 'success');
    } catch {
      this.toastService.show(this.transloco.translate('providers.stopDrawer.raw.copyFailed'), 'error');
    }
  }

  pickLocalized(entries: { lang: string; text: string }[] | undefined): string {
    return pickLocalizedText(entries, this.languageService.current());
  }

  formatClock(iso: string | null | undefined): string {
    if (!iso) return '—';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '—';
    return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
  }

  formatDelay(minutes: number | null): string {
    if (minutes === null) return '';
    const sign = minutes > 0 ? '+' : '−';
    return `${sign}${Math.abs(minutes)} min`;
  }

  drawerStopStatusDot(): LabelBadgeDot | null {
    const status = this.stopDrawer()?.diagnosticsStatus;
    if (status === 'OK') return 'green';
    if (status === 'ERROR') return 'red';
    return 'orange';
  }

  private startRefreshTimer(): void {
    this.stopRefreshTimer();
    this.refreshTimerSub = interval(1000)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.nowTick.set(Date.now());
        if (this.autoRefresh() && this.isDrawerOpen() && this.refreshCountdown() <= 0 && !this.isFetching()) {
          this.fetchDrawerContent();
        }
      });
  }

  private stopRefreshTimer(): void {
    this.refreshTimerSub?.unsubscribe();
    this.refreshTimerSub = null;
  }

  /** 404 = nothing published yet, not an error (mirrors fetchDiagnostics).
   *  A background-refresh failure keeps the last good payload and only flips
   *  lastError, so the drawer keeps showing stale-but-known content. */
  private fetchDrawerContent(): void {
    const drawer = this.stopDrawer();
    const f = this.feed();
    if (!drawer || !f) return;

    this.isFetching.set(true);
    this.contentDeliveryService
      .getStopContent(f.tenantId, this.routeFeedId, drawer.stopId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => {
          this.content.set(res.data);
          this.lastError.set(null);
          this.isFetching.set(false);
          this.fetchedAt.set(Date.now());
          this.nowTick.set(Date.now());
        },
        error: (err) => {
          this.isFetching.set(false);
          if (err?.status === 404) {
            this.content.set(null);
            this.lastError.set(null);
          } else {
            this.lastError.set(err);
          }
          this.fetchedAt.set(Date.now());
          this.nowTick.set(Date.now());
        },
      });
  }

  healthLabel(): string {
    const health = this.health();
    const t = (k: string, params?: Record<string, unknown>) => this.transloco.translate(`providers.diagnostics.${k}`, params);
    switch (health.status) {
      case 'no-snapshot':
        return t('noSnapshot');
      case 'failing':
        return t(health.error === 1 ? 'stopFailing' : 'stopsFailing', { count: health.error });
      case 'not-reporting':
        return t(health.unknown === 1 ? 'stopNotReporting' : 'stopsNotReporting', { count: health.unknown });
      case 'ok':
        return t('allStopsOk');
    }
  }

  /** 404 = no snapshot yet, never an error — soft-fails to null, no toast. */
  private fetchDiagnostics(): void {
    this.isDiagnosticsLoading.set(true);
    this.feedService
      .getDiagnostics<SiriDiagnosticsPayload>(this.routeFeedId)
      .pipe(
        catchError(() => of(null)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((res) => {
        this.diagnostics.set(res?.data ?? null);
        this.isDiagnosticsLoading.set(false);
      });
  }
}
